// App.jsx - Aggiornato con AuthContext e Sistema Cache Automatico
import React, { useState, useRef, useEffect } from 'react';
import Header from './components/Header';
import UserInfo from './components/UserInfo';
import TimesheetTable from './components/TimesheetTable';
import ReportsTable from './components/ReportsTable';
import UserRequests from './components/UserRequests';
import UserNavigation from './components/UserNavigation';
import UserDashboard from './components/UserDashboard';
import ReportForm from './components/ReportForm';
import Notification from './components/Notification';
import Login from './components/Login';
import AdminPanel from './components/Admin/AdminPanel';
import ResponsabileDashboard from './components/Responsabile/ResponsabileDashboard';
import OfflineManager from './components/OfflineManager';
import HRDashboard from './components/HR/HRDashboard';
import CapoOfficinaDashboard from './components/CapoOfficina/CapoOfficinaDashboard';
import './styles/fix-suggestions-mobile.css';

// ✅ NUOVO: Import AuthContext
import { AuthProvider, useAuth } from './contexts/AuthContext';

// ✅ NUOVO: Import Sistema Cache Automatico
import { handleAppUpdate, checkForceRefresh } from './utils/version';
import { cacheManager } from './utils/cacheManager';

import { getUserReportsByMonth } from './firebase';
import './index.css';
import './request.css';
import './admin-requests.css';
import './user-navigation.css';
import './components/userQRCode.css';
import './components/dashboard.css';
import './components/timekeepingScanner.css';

// CORRETTO: Funzione per determinare il mese e anno di default
const getDefaultMonthAndYear = () => {
  const currentDate = new Date();
  const currentDay = currentDate.getDate();
  const currentMonth = currentDate.getMonth() + 1; // getMonth returns 0-11
  const currentYear = currentDate.getFullYear();

  // LOGICA CORRETTA:
  // - Se siamo nei primi 4 giorni del mese (1-4), mostra il mese precedente
  // - Dal giorno 5 in poi, mostra sempre il mese corrente
  if (currentDay <= 4) {
    // Calcola il mese precedente
    let previousMonth = currentMonth - 1;
    let previousYear = currentYear;
    
    if (previousMonth === 0) {
      previousMonth = 12;
      previousYear = currentYear - 1;
    }
    
    return {
      month: previousMonth.toString(),
      year: previousYear.toString()
    };
  }
  
  // Dal giorno 5 in poi, mostra sempre il mese corrente
  return {
    month: currentMonth.toString(),
    year: currentYear.toString()
  };
};

// ✅ NUOVO: Componente principale dell'app (interno al Provider)
function AppContent() {
  // ✅ SOSTITUITO: Usa AuthContext invece di gestione manuale
  const { currentUser, userRole, loading } = useAuth();
  
  const defaultSelection = getDefaultMonthAndYear();
  const [isAdminView, setIsAdminView] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');

  // App states (visible only when logged in)
  const [selectedMonth, setSelectedMonth] = useState(defaultSelection.month);
  const [selectedYear, setSelectedYear] = useState(defaultSelection.year);
  const [reports, setReports] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [isReportFormVisible, setIsReportFormVisible] = useState(false);
  const [formPosition, setFormPosition] = useState(null);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [notificationType, setNotificationType] = useState("success");
  const [highlightedRow, setHighlightedRow] = useState(null);
  const [isLoadingReports, setIsLoadingReports] = useState(false);
  const tableRowsRef = useRef({});

  // ✅ NUOVO: Inizializzazione Sistema Cache Automatico
  useEffect(() => {
    const initializeApp = async () => {
      try {
        console.log('🚀 App: Inizializzazione sistema cache automatico');
        
        // 1. Controlla immediatamente aggiornamenti
        await handleAppUpdate();
        
        // 2. Pulisci cache scadute
        await cacheManager.cleanExpiredCaches();
        
        // 3. Valida integrità cache
        await cacheManager.validateCacheIntegrity();
        
        // 4. Controlla se serve refresh forzato
        checkForceRefresh();
        
        // 5. Configura controlli automatici ogni 30 minuti (ottimizzato)
        const updateInterval = setInterval(async () => {
          console.log('🔄 App: Controllo automatico aggiornamenti');
          await handleAppUpdate();
        }, 30 * 60 * 1000); // 30 minuti
        
        // 6. Configura pulizia cache ogni 2 ore
        const cleanupInterval = setInterval(async () => {
          console.log('🧹 App: Pulizia automatica cache');
          await cacheManager.cleanExpiredCaches();
        }, 2 * 60 * 60 * 1000); // 2 ore
        
        // Cleanup
        return () => {
          clearInterval(updateInterval);
          clearInterval(cleanupInterval);
        };
      } catch (error) {
        console.error('❌ App: Errore inizializzazione sistema cache:', error);
      }
    };

    initializeApp();
    
    // ✅ NUOVO: Gestisci eventi browser per controlli intelligenti
    const handleVisibilityChange = async () => {
      if (!document.hidden) {
        console.log('👁️ App: Pagina tornata visibile - controllo aggiornamenti');
        await handleAppUpdate();
      }
    };
    
    const handleFocus = async () => {
      console.log('🎯 App: Finestra tornata in focus - controllo aggiornamenti');
      await handleAppUpdate();
    };
    
    const handleOnline = async () => {
      console.log('🌐 App: Connessione ripristinata - controllo aggiornamenti');
      await handleAppUpdate();
    };
    
    // Aggiungi event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('online', handleOnline);
    
    // Cleanup event listeners
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  // ✅ SEMPLIFICATO: Gestione automatica della vista elevata quando cambia il ruolo
  useEffect(() => {
    if (userRole === 'admin' || userRole === 'responsabile' || userRole === 'hr' || userRole === 'capo_officina') {
      setIsAdminView(true);
      console.log(`App: Vista elevata attivata automaticamente per ruolo: ${userRole}`);
    } else if (userRole === 'user') {
      setIsAdminView(false);
      console.log('App: Vista utente attivata');
    }
  }, [userRole]);

  // Load user reports from database
  useEffect(() => {
    const loadUserReports = async () => {
      if (!currentUser || isAdminView) return;
      
      // Only load reports in the hours tab
      if (activeTab !== 'hours') return;
      
      setIsLoadingReports(true);
      try {
        console.log(`App: Loading reports for userId=${currentUser.uid}, month=${selectedMonth}, year=${selectedYear}`);
        
        // Normalize month (remove any prefixes and leading zeros)
        let normalizedMonth = selectedMonth.toString().replace(/^prev-/, '').replace(/^0+/, '');
        
        // If month is empty after normalization, use the original value
        if (!normalizedMonth) normalizedMonth = selectedMonth;
        
        // Format with leading zero for consistency
        const formattedMonth = normalizedMonth.length === 1 ? normalizedMonth.padStart(2, '0') : normalizedMonth;
          
        const updatedReports = await getUserReportsByMonth(currentUser.uid, formattedMonth, selectedYear);
        console.log("App: Reports loaded:", updatedReports);
        setReports(updatedReports || []);
      } catch (error) {
        console.error("App: Error loading reports:", error);
      } finally {
        setIsLoadingReports(false);
      }
    };

    loadUserReports();
  }, [currentUser, selectedMonth, selectedYear, activeTab, isAdminView]);

  // Handle report submission
  const handleReportSubmit = async (reportData) => {
    try {
      console.log("App: Report submitted:", reportData);
      setNotificationMessage("Segnalazione inviata con successo!");
      setNotificationType("success");
      setShowNotification(true);
      
      // Close form
      setIsReportFormVisible(false);
      setSelectedDate(null);
      
      // Reload reports after a short delay to allow backend processing
      setTimeout(async () => {
        try {
          // Normalize month (remove any prefixes and leading zeros)
          let normalizedMonth = selectedMonth.toString().replace(/^prev-/, '').replace(/^0+/, '');
          
          // If month is empty after normalization, use the original value
          if (!normalizedMonth) normalizedMonth = selectedMonth;
          
          // Format with leading zero for consistency
          const formattedMonth = normalizedMonth.length === 1 ? normalizedMonth.padStart(2, '0') : normalizedMonth;
          
          const updatedReports = await getUserReportsByMonth(currentUser.uid, formattedMonth, selectedYear);
          console.log("App: Reports reloaded:", updatedReports);
          setReports(updatedReports || []);
        } catch (error) {
          console.error("App: Error reloading reports:", error);
        }
      }, 1000);
    } catch (error) {
      console.error("App: Error handling report:", error);
      setNotificationMessage("An error occurred. Please try again later.");
      setNotificationType("error");
      setShowNotification(true);
    }
  };

  // ✅ AGGIORNATO: Function to toggle between user and elevated view (admin/responsabile)
  const toggleAdminView = () => {
    const viewType = userRole === 'admin' ? 'admin' : 'responsabile';
    console.log("App: View change", isAdminView ? `${viewType} -> user` : `user -> ${viewType}`);
    setIsAdminView(!isAdminView);
  };

  // Function to change tab in user dashboard
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handleLeaveRequestSubmit = async (requestData) => {
    try {
      console.log("App: Leave request submitted:", requestData);
      setNotificationMessage("Richiesta inviata con successo!");
      setNotificationType("success");
      setShowNotification(true);
    } catch (error) {
      console.error("App: Error handling leave request:", error);
      setNotificationMessage("Errore nell'invio della richiesta. Riprova più tardi.");
      setNotificationType("error");
      setShowNotification(true);
    }
  };

  // ✅ AGGIORNATO: Mostra loading mentre AuthContext carica
  if (loading) {
    return (
      <div className="App">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          flexDirection: 'column',
          gap: '20px'
        }}>
          <div className="loading-spinner"></div>
          <p>Caricamento...</p>
        </div>
      </div>
    );
  }

  // ✅ AGGIORNATO: Usa currentUser da AuthContext
  if (!currentUser) {
    // Show login if not authenticated
    return <Login onLogin={() => {
      // La gestione del login è ora automatica tramite AuthContext
      console.log('Login completato, AuthContext gestirà l\'aggiornamento');
    }} />;
  }

  return (
    <div className="App">
      {/* ✅ AGGIORNATO: Passa i dati da AuthContext */}
     
      <Header 
  userRole={userRole} 
  isAdminView={isAdminView} 
  onToggleView={(userRole === 'admin' || userRole === 'responsabile' || userRole === 'hr' || userRole === 'capo_officina') ? toggleAdminView : null} 
/>

      {/* Renderizzazione condizionale basata su ruolo e vista */}
      {isAdminView && userRole === 'admin' ? (
  <AdminPanel />
) : isAdminView && userRole === 'responsabile' ? (
  <ResponsabileDashboard />
) : isAdminView && userRole === 'hr' ? (
  <HRDashboard />
) : isAdminView && userRole === 'capo_officina' ? (
  <CapoOfficinaDashboard />
) : (
  // Show user view
        <div className="container">
          <Notification
            message={notificationMessage}
            isVisible={showNotification}
            onClose={() => setShowNotification(false)}
            type={notificationType}
          />
          
          {/* Navigation bar for user dashboard */}
          <UserNavigation 
            activeTab={activeTab} 
            onTabChange={handleTabChange} 
          />

          {activeTab === 'dashboard' ? (
            // Dashboard tab content
            <UserDashboard />
          ) : activeTab === 'hours' ? (
            // Hours management tab content  
            <div>
              <UserInfo 
                selectedMonth={selectedMonth}
                setSelectedMonth={setSelectedMonth}
                selectedYear={selectedYear}
                setSelectedYear={setSelectedYear}
              />
             <TimesheetTable
  selectedMonth={selectedMonth}
  selectedYear={selectedYear}
  onReportError={(date) => {
    console.log('📝 Apertura form segnalazione per data:', date);
    setSelectedDate(date);
    setIsReportFormVisible(true);
  }}
  highlightedRow={highlightedRow}
  rowRef={(date, element) => {
    tableRowsRef.current[date] = element;
  }}
/>
              
              <ReportsTable 
                reports={reports}
                selectedMonth={selectedMonth}
                selectedYear={selectedYear}
                tableRowsRef={tableRowsRef}
                highlightedRow={highlightedRow}
                setHighlightedRow={setHighlightedRow}
                onCellClick={(date, position) => {
                  setSelectedDate(date);
                  setFormPosition(position);
                  setIsReportFormVisible(true);
                }}
              />
              
              {isReportFormVisible && (
  <ReportForm
    isVisible={isReportFormVisible}  // ✅ AGGIUNTO: Passa esplicitamente isVisible
    selectedDate={selectedDate}
    position={formPosition}
    onSubmit={handleReportSubmit}
    onClose={() => {
      setIsReportFormVisible(false);
      setSelectedDate(null);
    }}
  />
)}
            </div>
          ) : activeTab === 'requests' ? (
            // Requests tab content
            <UserRequests onRequestSubmit={handleLeaveRequestSubmit} />
          ) : null}
          
          {/* Offline Manager */}
          <OfflineManager />
        </div>
      )}
    </div>
  );
}

// ✅ NUOVO: Componente App principale con Provider
function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;